/**
 * Manual card intake.
 *
 * This page is for operator-selected local files, not camera capture. A front
 * and back image are uploaded as-is, then the API creates one unverified card,
 * marks Image Approval as pending, and places that card in the selected
 * Storage_Batch through the Storage_Service putaway path.
 */

import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useCatalogApi, type ManualCardUploadResponse } from '../catalog/catalog-api';
import {
  CONFIRM_CONFIDENCE_THRESHOLD,
  type CardIdentity,
  type CardRecord,
  type GradingResponse,
  type IdentificationResponse,
} from '../shared/types';

interface ReviewedImage {
  imageId: string;
  s3Key: string;
  identification: IdentificationResponse;
  grading: GradingResponse;
  /** Operator-supplied identity. Required when confidence < threshold. */
  override?: CardIdentity;
}

type UploadState =
  | { status: 'idle' }
  | { status: 'uploading'; message: string; completed: number; total: number }
  | { status: 'done'; cards: UploadedManualCard[]; batchId: string }
  | { status: 'error'; message: string; cards: UploadedManualCard[] };

type IntakeMode = 'single' | 'multi';

export interface ManualUploadPair {
  id: string;
  front: File;
  back: File;
}

export interface ManualUploadFileIssue {
  fileName: string;
  message: string;
}

export interface ManualUploadPairParseResult {
  pairs: ManualUploadPair[];
  issues: ManualUploadFileIssue[];
}

interface UploadedManualCard {
  pairId: string;
  card: CardRecord | undefined;
  batchId: string;
}

/** Top-level New card intake page rendered by the Inventory router. */
export function IntakePage(): JSX.Element {
  const catalog = useCatalogApi();
  const [mode, setMode] = useState<IntakeMode>('single');
  const [batchId, setBatchId] = useState('');
  const [frontFile, setFrontFile] = useState<File | null>(null);
  const [backFile, setBackFile] = useState<File | null>(null);
  const [multiFiles, setMultiFiles] = useState<File[]>([]);
  const [state, setState] = useState<UploadState>({ status: 'idle' });
  const parsedPairs = useMemo(() => parseManualUploadPairs(multiFiles), [multiFiles]);

  const onSubmitSingle = useCallback(async () => {
    const normalizedBatchId = normalizeBatchId(batchId);
    if (normalizedBatchId === null) {
      setState({ status: 'error', message: 'Enter a valid batch ID or short code.', cards: [] });
      return;
    }
    if (frontFile === null || backFile === null) {
      setState({
        status: 'error',
        message: 'Choose both a front image and a back image.',
        cards: [],
      });
      return;
    }

    try {
      const uploaded = await uploadManualPair({
        catalog,
        batchId: normalizedBatchId,
        pair: { id: 'single', front: frontFile, back: backFile },
        onProgress: (message) => setState({ status: 'uploading', message, completed: 0, total: 1 }),
      });
      setFrontFile(null);
      setBackFile(null);
      setState({ status: 'done', cards: [uploaded], batchId: uploaded.batchId });
    } catch (err) {
      setState({
        status: 'error',
        message: err instanceof Error ? err.message : String(err),
        cards: [],
      });
    }
  }, [backFile, batchId, catalog, frontFile]);

  const onSubmitMulti = useCallback(async () => {
    const normalizedBatchId = normalizeBatchId(batchId);
    if (normalizedBatchId === null) {
      setState({ status: 'error', message: 'Enter a valid batch ID or short code.', cards: [] });
      return;
    }
    if (parsedPairs.issues.length > 0) {
      setState({
        status: 'error',
        message: 'Fix the filename pairing issues before uploading this batch.',
        cards: [],
      });
      return;
    }
    if (parsedPairs.pairs.length === 0) {
      setState({
        status: 'error',
        message: 'Choose paired files named identifier-front.jpg and identifier-back.jpg.',
        cards: [],
      });
      return;
    }

    const uploaded: UploadedManualCard[] = [];
    for (let index = 0; index < parsedPairs.pairs.length; index += 1) {
      const pair = parsedPairs.pairs[index]!;
      try {
        const card = await uploadManualPair({
          catalog,
          batchId: normalizedBatchId,
          pair,
          onProgress: (message) =>
            setState({
              status: 'uploading',
              message: `${pair.id}: ${message}`,
              completed: uploaded.length,
              total: parsedPairs.pairs.length,
            }),
        });
        uploaded.push(card);
      } catch (err) {
        setState({
          status: 'error',
          message: `${pair.id}: ${err instanceof Error ? err.message : String(err)}`,
          cards: uploaded,
        });
        return;
      }
    }

    setMultiFiles([]);
    setState({ status: 'done', cards: uploaded, batchId: normalizedBatchId });
  }, [batchId, catalog, parsedPairs]);

  const busy = state.status === 'uploading';
  const canUploadMulti = parsedPairs.pairs.length > 0 && parsedPairs.issues.length === 0;

  return (
    <section>
      <h2>New card intake</h2>
      <p className="tcvs-page-intro">
        Upload front and back images from this computer, then place the new card records into an
        existing batch. Each card will start without a verified identity and pending Image Approval.
      </p>

      <div
        style={{
          display: 'grid',
          gap: 16,
          maxWidth: 860,
        }}
      >
        <label style={{ display: 'grid', gap: 6 }}>
          Batch ID
          <input
            type="text"
            value={batchId}
            disabled={busy}
            placeholder="tcvs:batch:01... or 01..."
            onChange={(event) => setBatchId(event.target.value)}
          />
        </label>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            type="button"
            className={mode === 'single' ? undefined : 'tcvs-btn-secondary'}
            aria-pressed={mode === 'single'}
            disabled={busy}
            onClick={() => {
              setMode('single');
              setState({ status: 'idle' });
            }}
          >
            Single card
          </button>
          <button
            type="button"
            className={mode === 'multi' ? undefined : 'tcvs-btn-secondary'}
            aria-pressed={mode === 'multi'}
            disabled={busy}
            onClick={() => {
              setMode('multi');
              setState({ status: 'idle' });
            }}
          >
            Multi-card batch
          </button>
        </div>

        {mode === 'single' ? (
          <>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                gap: 16,
              }}
            >
              <ImagePicker
                label="Front image"
                file={frontFile}
                disabled={busy}
                onFile={setFrontFile}
              />
              <ImagePicker
                label="Back image"
                file={backFile}
                disabled={busy}
                onFile={setBackFile}
              />
            </div>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <button type="button" disabled={busy} onClick={() => void onSubmitSingle()}>
                Upload card to batch
              </button>
              {busy ? <UploadProgress state={state} /> : null}
            </div>
          </>
        ) : (
          <MultiCardUploadPanel
            files={multiFiles}
            parsed={parsedPairs}
            busy={busy}
            canUpload={canUploadMulti}
            onFiles={(files) => {
              setMultiFiles(files);
              setState({ status: 'idle' });
            }}
            onUpload={() => void onSubmitMulti()}
            state={state}
          />
        )}

        {state.status === 'error' ? <p className="tcvs-error">Error: {state.message}</p> : null}
        {state.status === 'done' ? (
          <SuccessMessage cards={state.cards} batchId={state.batchId} />
        ) : null}
        {state.status === 'error' && state.cards.length > 0 ? (
          <PartialUploadMessage cards={state.cards} />
        ) : null}
      </div>
    </section>
  );
}

/** True iff every image has a confirmable identity. Mirrors legacy `confirmAllowed`. */
export function allowConfirm(reviewed: readonly ReviewedImage[]): boolean {
  if (reviewed.length === 0) return false;
  return reviewed.every((img) => {
    if (img.identification.confidence >= CONFIRM_CONFIDENCE_THRESHOLD) return true;
    if (img.override === undefined) return false;
    return Object.values(img.override).every((v) => v.length > 0);
  });
}

function ImagePicker({
  label,
  file,
  disabled,
  onFile,
}: {
  label: string;
  file: File | null;
  disabled: boolean;
  onFile: (file: File | null) => void;
}): JSX.Element {
  return (
    <label
      style={{
        display: 'grid',
        gap: 8,
        alignContent: 'start',
        padding: 12,
        border: '1px solid var(--tcvs-border)',
        borderRadius: 8,
        background: 'var(--tcvs-surface-alt)',
      }}
    >
      <span>{label}</span>
      <input
        type="file"
        accept="image/jpeg,image/png,image/webp"
        disabled={disabled}
        onChange={(event) => onFile(event.target.files?.[0] ?? null)}
      />
      {file !== null ? (
        <span className="tcvs-muted">
          {file.name} ({formatBytes(file.size)})
        </span>
      ) : null}
    </label>
  );
}

function MultiCardUploadPanel({
  files,
  parsed,
  busy,
  canUpload,
  onFiles,
  onUpload,
  state,
}: {
  files: readonly File[];
  parsed: ManualUploadPairParseResult;
  busy: boolean;
  canUpload: boolean;
  onFiles: (files: File[]) => void;
  onUpload: () => void;
  state: UploadState;
}): JSX.Element {
  return (
    <section className="tcvs-card">
      <label style={{ display: 'grid', gap: 8 }}>
        Paired card images
        <input
          type="file"
          accept="image/jpeg"
          multiple
          disabled={busy}
          onChange={(event) => onFiles(Array.from(event.target.files ?? []))}
        />
      </label>
      <p className="tcvs-muted" style={{ marginTop: 8 }}>
        Select files named like 2FDD4S-front.jpg and 2FDD4S-back.jpg. Matching identifiers become
        one card each.
      </p>
      {files.length > 0 ? (
        <p className="tcvs-muted" style={{ marginBottom: 0 }}>
          {files.length} file{files.length === 1 ? '' : 's'} selected · {parsed.pairs.length} pair
          {parsed.pairs.length === 1 ? '' : 's'} ready
        </p>
      ) : null}
      {parsed.pairs.length > 0 ? <PairPreview pairs={parsed.pairs} /> : null}
      {parsed.issues.length > 0 ? <PairIssues issues={parsed.issues} /> : null}
      <div
        style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 12 }}
      >
        <button type="button" disabled={busy || !canUpload} onClick={onUpload}>
          Upload paired cards to batch
        </button>
        {busy ? <UploadProgress state={state} /> : null}
      </div>
    </section>
  );
}

function PairPreview({ pairs }: { pairs: readonly ManualUploadPair[] }): JSX.Element {
  const shown = pairs.slice(0, 12);
  return (
    <div style={{ overflowX: 'auto', marginTop: 12 }}>
      <table className="tcvs-table">
        <thead>
          <tr>
            <th>Identifier</th>
            <th>Front</th>
            <th>Back</th>
          </tr>
        </thead>
        <tbody>
          {shown.map((pair) => (
            <tr key={pair.id}>
              <td className="tcvs-mono">{pair.id}</td>
              <td>{pair.front.name}</td>
              <td>{pair.back.name}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {pairs.length > shown.length ? (
        <p className="tcvs-muted" style={{ marginBottom: 0 }}>
          Showing first {shown.length} of {pairs.length} pairs.
        </p>
      ) : null}
    </div>
  );
}

function PairIssues({ issues }: { issues: readonly ManualUploadFileIssue[] }): JSX.Element {
  return (
    <section
      className="tcvs-card-warning"
      style={{
        marginTop: 12,
        padding: 12,
        border: '1px solid var(--tcvs-warning-border)',
        borderRadius: 8,
      }}
    >
      <strong>Pairing issues</strong>
      <ul className="tcvs-muted" style={{ marginBottom: 0 }}>
        {issues.map((issue, index) => (
          <li key={`${issue.fileName}-${index}`}>
            <span className="tcvs-mono">{issue.fileName}</span>: {issue.message}
          </li>
        ))}
      </ul>
    </section>
  );
}

function UploadProgress({ state }: { state: UploadState }): JSX.Element | null {
  if (state.status !== 'uploading') return null;
  const detail = state.total > 1 ? ` (${state.completed}/${state.total} complete)` : '';
  return (
    <span>
      {state.message}
      {detail}
    </span>
  );
}

function SuccessMessage({
  cards,
  batchId,
}: {
  cards: readonly UploadedManualCard[];
  batchId: string;
}): JSX.Element {
  const batchDetailHref = `/storage/batches/${encodeURIComponent(entityIdBody(batchId))}`;
  return (
    <div className="tcvs-success">
      {cards.length === 1
        ? 'Card uploaded successfully.'
        : `${cards.length} cards uploaded successfully.`}{' '}
      {cards.length === 1 ? <UploadedCardLink item={cards[0]!} /> : null}
      {cards.length === 1 ? ' was' : 'They were'} placed in{' '}
      <Link to={batchDetailHref}>{batchId}</Link>.
      {cards.length > 1 ? <UploadedCardsList cards={cards} /> : null}
    </div>
  );
}

function PartialUploadMessage({ cards }: { cards: readonly UploadedManualCard[] }): JSX.Element {
  return (
    <section className="tcvs-card tcvs-card-warning">
      <strong>
        {cards.length} card{cards.length === 1 ? '' : 's'} uploaded before the error.
      </strong>
      <UploadedCardsList cards={cards} />
    </section>
  );
}

function UploadedCardsList({ cards }: { cards: readonly UploadedManualCard[] }): JSX.Element {
  return (
    <ul style={{ marginBottom: 0 }}>
      {cards.map((item) => (
        <li key={item.pairId}>
          <span className="tcvs-mono">{item.pairId}</span> · <UploadedCardLink item={item} />
        </li>
      ))}
    </ul>
  );
}

function UploadedCardLink({ item }: { item: UploadedManualCard }): JSX.Element {
  if (item.card === undefined) return <span>card record</span>;
  return <Link to={`/catalog/${item.card.cardId}`}>{item.card.shortCode ?? item.card.cardId}</Link>;
}

function normalizeBatchId(value: string): string | null {
  const trimmed = value.trim();
  if (/^tcvs:batch:[A-Za-z0-9_-]+$/.test(trimmed)) return trimmed;
  if (/^[A-Za-z0-9_-]+$/.test(trimmed)) return `tcvs:batch:${trimmed}`;
  return null;
}

function entityIdBody(value: string): string {
  const lastColon = value.lastIndexOf(':');
  return lastColon === -1 ? value : value.slice(lastColon + 1);
}

export function parseManualUploadPairs(files: readonly File[]): ManualUploadPairParseResult {
  const byId = new Map<string, { front?: File; back?: File }>();
  const issues: ManualUploadFileIssue[] = [];
  const seen = new Set<string>();

  for (const file of files) {
    const match = /^([A-Za-z0-9]+)-(front|back)\.jpg$/i.exec(file.name);
    if (match === null) {
      issues.push({
        fileName: file.name,
        message: 'Expected identifier-front.jpg or identifier-back.jpg.',
      });
      continue;
    }
    const id = match[1]!;
    const side = match[2]!.toLowerCase() as 'front' | 'back';
    const key = `${id}:${side}`;
    if (seen.has(key)) {
      issues.push({ fileName: file.name, message: `Duplicate ${side} image for ${id}.` });
      continue;
    }
    seen.add(key);
    const group = byId.get(id) ?? {};
    group[side] = file;
    byId.set(id, group);
  }

  const pairs: ManualUploadPair[] = [];
  for (const [id, group] of [...byId].sort(([a], [b]) =>
    a.localeCompare(b, undefined, { numeric: true }),
  )) {
    if (group.front === undefined) {
      issues.push({ fileName: `${id}-front.jpg`, message: `Missing front image for ${id}.` });
    }
    if (group.back === undefined) {
      issues.push({ fileName: `${id}-back.jpg`, message: `Missing back image for ${id}.` });
    }
    if (group.front !== undefined && group.back !== undefined) {
      pairs.push({ id, front: group.front, back: group.back });
    }
  }

  return { pairs, issues };
}

async function uploadManualPair({
  catalog,
  batchId,
  pair,
  onProgress,
}: {
  catalog: ReturnType<typeof useCatalogApi>;
  batchId: string;
  pair: ManualUploadPair;
  onProgress: (message: string) => void;
}): Promise<UploadedManualCard> {
  onProgress('Preparing upload...');
  const created = await catalog.createManualCardUpload(batchId, pair.front, pair.back);
  if (!created.ok) {
    throw new Error(created.message ?? created.error);
  }

  onProgress('Uploading front image...');
  await uploadToPresignedPost(created.data.uploads.front, pair.front);
  onProgress('Uploading back image...');
  await uploadToPresignedPost(created.data.uploads.back, pair.back);

  onProgress('Creating card in batch...');
  const completed = await catalog.completeManualCardUpload(created.data.cardId, batchId, {
    front: created.data.uploads.front.s3Key,
    back: created.data.uploads.back.s3Key,
  });
  if (!completed.ok) {
    throw new Error(completed.message ?? completed.error);
  }

  return { pairId: pair.id, card: completed.data.card, batchId: completed.data.batchId };
}

async function uploadToPresignedPost(
  upload: ManualCardUploadResponse['uploads']['front'],
  file: File,
): Promise<void> {
  const form = new FormData();
  for (const [key, value] of Object.entries(upload.uploadUrl.fields)) {
    form.append(key, value);
  }
  form.append('file', file);

  const response = await fetch(upload.uploadUrl.url, { method: 'POST', body: form });
  if (!response.ok) {
    throw new Error(`Image upload failed (${response.status})`);
  }
}

function formatBytes(value: number): string {
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}
